import {
  PdfGen,
  PdfRect,
  PdfPoint,
  PdfSize,
  PdfUnit,
  PdfBrush,
  PdfPen,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutSingleGen } from '../layoutSingle';
import { OptsSingle } from '../optsSingle';
import { GUtil } from '../../pdf/gutil';
import { genSingle, fixGenArray } from '../../core/genHelper';
import { C } from '../../core/c';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';
import '../../pdf/draw/shapes';

const ALL_COLORS = [
  ['#9b518c', '#b0709b', '#c690aa', '#dcafb8', '#f1cfc6'],
  ['#e3bd6e', '#dbb361', '#d2a954', '#ca9f45', '#c29536'],
  ['#3d7648', '#458c6d', '#4ea292', '#83b2b3', '#b1c2d5'],
  ['#fd8381', '#f46e85', '#ea5888', '#e03d8a', '#d6138b'],
  ['#480e9c', '#6346b5', '#8170ce', '#a299e5', '#c5c2fc'],
  ['#f5b0f3', '#ee9cee', '#e787e9', '#df71e3', '#d658de'],
  ['#6ba2e0', '#6bc5e0', '#6baee0', '#6b96e0', '#6b7fe0'],
  ['#fbba68', '#f99602', '#e5870e', '#d17914', '#d8820d'],
  ['#c9e7ff', '#c9f7ff', '#c9ecff', '#c9e2ff', '#c9d7ff'],
  ['#cae05e', '#e0cf5e', '#d7e05e', '#bde05e', '#a3e05e'],
  ['#c8d123', '#d1a623', '#d1c923', '#b7d123', '#94d123'],
];

export enum ESqType {
  None,
  Ladder,
  Dice,
}

export class Sq {
  public Type: ESqType = ESqType.None;
  public DiceCount = 0;
}

export class HraPostupne implements ILayoutSingleGen {
  public async generate(pdf: PdfGen, opts: OptsSingle): Promise<void> {
    const totalSheets = 3;

    for (let i = 0; i < totalSheets; i++) {
      const countX = 4 + (i % 3);
      this.GenerateHelper(pdf, opts, countX);
    }
  }

  private GenerateHelper(pdf: PdfGen, opts: OptsSingle, countX: number): void {
    const colors = GUtil.randomBrushes(opts.data.random, ALL_COLORS);
    const page = pdf.addPage();

    const margin = PdfUnit.FromMillimeters(10);
    const pageWidth = PdfSize.A4.width - 2 * margin;
    const pageHeight = PdfSize.A4.height - 2 * margin;

    const ss = pageWidth / (countX * 2 - 1);
    const size = new PdfSize(ss, ss);
    const countY = Math.floor(pageHeight / ss);

    const baseRectX = margin;
    const baseRectY = margin + (pageHeight - size.height * countY) / 2;

    const gm: C[] = [];

    for (let x = 0; x < countX; x++) {
      for (let y = 0; y < countY; y++) {
        gm.push(new C(x * 2, x % 2 === 0 ? y : countY - 1 - y));
      }

      if (x + 1 < countX) {
        gm.push(new C(x * 2 + 1, x % 2 === 0 ? countY - 1 : 0));
      }
    }

    const game = this.GenerateGame(gm, countX, countY, opts);

    for (let i = gm.length - 1; i >= 0; i--) {
      const r = new PdfRect(
        baseRectX + size.width * gm[i].x,
        baseRectY + size.height * gm[i].y,
        size.width,
        size.height
      );

      let generate = false;

      if (i === 0) {
        const inflated = r.inflate(5);
        page.drawRectangle(GUtil.basePen, PdfBrush.White, inflated);
        const startText = opts.data.lang?.Generator_Start ?? 'START';
        page.drawString(
          startText,
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          inflated,
          ETextAlign.Center
        );
      } else if (i === gm.length - 1) {
        const finish = 5;
        const sz1 = new PdfSize(r.width / finish, r.height / finish);
        const sz2 = new PdfSize(sz1.width / 2, sz1.height / 2);
        for (let x = 0; x < finish; x++) {
          for (let y = 0; y < finish; y++) {
            const r1 = new PdfRect(
              r.x + x * sz1.width,
              r.y + y * sz1.height,
              sz2.width,
              sz2.height
            );
            const r2 = new PdfRect(
              r.x + x * sz1.width + sz2.width,
              r.y + y * sz1.height + sz2.height,
              sz2.width,
              sz2.height
            );
            page.drawRectangle(null, PdfBrush.Black, r1);
            page.drawRectangle(null, PdfBrush.Black, r2);
          }
        }
        page.drawRectangle(GUtil.basePen, null, r);
      } else if (game[i].Type === ESqType.Dice) {
        page.drawEllipse(null, colors[i % colors.length], r);
        page.drawDicesV(GUtil.basePen, r, r.width / 10, game[i].DiceCount);
      } else {
        generate = true;
      }

      if (generate) {
        const gen = genSingle(opts.data, opts.generator, 1, false, false)[0];
        const texts = fixGenArray(gen.texts).map((t) => t.replace(/=+\s*$/, '').trim());

        page.drawEllipse(null, colors[i % colors.length], r);
        const font = page.adjustFont(r.width * 0.9, texts, GUtil.baseFont);
        page.drawStrings(texts, font, PdfBrush.Black, null, r, ETextAlign.Center);
      }

      if (game[i].Type === ESqType.Ladder) {
        const p1 = new PdfPoint(r.centerX + size.width * 0.5, r.centerY);
        const p2 = new PdfPoint(r.centerX + size.width * 2 - size.width * 0.5, r.centerY);

        const p1a = new PdfPoint(p1.x, p1.y - size.width / 8);
        const p2a = new PdfPoint(p2.x, p2.y - size.width / 8);

        const p1b = new PdfPoint(p1.x, p1.y + size.width / 8);
        const p2b = new PdfPoint(p2.x, p2.y + size.width / 8);

        page.drawLine(GUtil.basePen, p1a, p2a);
        page.drawLine(GUtil.basePen, p1b, p2b);

        const c = new PdfPoint(r.centerX + size.width, r.centerY);

        for (let j = -4; j <= 4; j++) {
          const pp1 = new PdfPoint(c.x + (j * size.width) / 10, c.y - size.width / 8);
          const pp2 = new PdfPoint(c.x + (j * size.width) / 10, c.y + size.width / 8);

          page.drawLine(GUtil.basePen, pp1, pp2);
        }
      }
    }
  }

  private GenerateGame(gm: C[], countX: number, countY: number, opts: OptsSingle): Sq[] {
    const dict = new Map<string, Sq>();

    for (const c of gm) {
      dict.set(c.toHash(), new Sq());
    }

    const array = Array.from(dict.values());

    const ladders = Array.from({ length: countY - 2 }, (_, i) => i + 1).sort(
      () => opts.data.random.next(0, 2) - 0.5
    );

    for (let i = 0; i < countX - 1; i++) {
      const key = new C(i * 2, ladders[i]).toHash();
      const sq = dict.get(key);
      if (sq) sq.Type = ESqType.Ladder;
    }

    for (let i = 0; i < Math.floor(gm.length / 7); i++) {
      const index = 1 + opts.data.random.next(0, gm.length - 1);
      const sq = array[index];
      if (sq.Type === ESqType.None) {
        sq.Type = ESqType.Dice;
        sq.DiceCount = 1 + opts.data.random.next(0, 6);
      }
    }

    return array;
  }
}
